import { z } from 'zod';
import { paToolAvailabilitySchema } from './pa-delegation';
import { paOperationSemanticsSchema } from './pa-operation-semantics';

export const paToolPermissionSchema = z.object({
  server_id: z.string().uuid(),
  tool_name: z.string().min(1).max(255),
  enabled: z.boolean(),
  bulk_allowed: z.boolean().nullable(),
});

export const paLegacyBindingsSaveSchema = z.object({
  bindings: z.array(z.object({
    function_id: z.string().uuid(),
    is_enabled: z.boolean().default(true),
  })).max(10000),
});

export const paConfigurationSaveSchema = z.object({
  assigned_user_id: z.string().uuid().optional(),
  create_request_id: z.string().uuid().optional(),
  tool_permissions: z.array(paToolPermissionSchema).max(10000).optional(),
  name: z.string().trim().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  display_name: z.string().optional(),
  avatar_icon: z.string().optional(),
  color_theme: z.string().optional(),
  model: z.string().optional(),
  temperature: z.number().min(0).max(2).optional(),
  max_tokens: z.number().int().positive().optional(),
  system_prompt: z.string().min(1).optional(),
  rag_enabled: z.boolean().optional(),
  rag_inferential_enabled: z.boolean().optional(),
  rag_synthesis_mode: z.string().optional(),
  provider_id: z.string().uuid().nullable().optional(),
  llm_provider: z.string().optional(),
  llm_config: z.record(z.unknown()).optional(),
  custom_endpoint_id: z.string().uuid().nullable().optional(),
  custom_model_id: z.string().uuid().nullable().optional(),
  tenant_context_enabled: z.boolean().optional(),
  mcp_binding_config: z.record(z.unknown()).optional(),
  transcription_enabled: z.boolean().optional(),
  sentiment_enabled: z.boolean().optional(),
  context_memory_enabled: z.boolean().optional(),
  semantic_memory_enabled: z.boolean().optional(),
  is_active: z.boolean().optional(),
  pre_call_hooks: z.record(z.boolean()).optional(),
  acts_as_user_ids: z.array(z.string().uuid()).optional(),
  allowed_sender_ids: z.array(z.string().uuid()).nullable().optional(),
});

export const paConfigurationToolSchema = z.object({
  id: z.string().uuid(),
  tool_name: z.string(),
  description: z.string(),
  mcp_server_id: z.string().uuid(),
  server_name: z.string(),
  is_internal: z.boolean(),
  category: z.string().nullable(),
  tags: z.array(z.string()),
  enabled: z.boolean(),
  operation_semantics: paOperationSemanticsSchema.nullable(),
});

export const paConfigurationCatalogResponseSchema = z.object({
  tools: z.array(paConfigurationToolSchema),
  permissions: z.array(paToolPermissionSchema),
  users: z.array(z.object({
    id: z.string().uuid(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.string(),
  })),
  max_tools: z.number().int(),
  can_manage: z.boolean(),
  assistant_id: z.string().uuid().nullable(),
  ai_agent_id: z.string().uuid().nullable(),
  assigned_user_id: z.string().uuid().nullable(),
  authorization_mode: z.enum(['delegated', 'intersection']).nullable(),
  availability: z.array(paToolAvailabilitySchema),
  availability_error: z.string().nullable(),
});

export const paConfigurationSaveResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    id: z.string().uuid(),
    ai_agent_id: z.string().uuid(),
  }).passthrough().optional(),
  warning: z.string().optional(),
});

export type PaToolPermission = z.infer<typeof paToolPermissionSchema>;
export type PaConfigurationSave = z.infer<typeof paConfigurationSaveSchema>;
export type PaConfigurationCatalogResponse = z.infer<typeof paConfigurationCatalogResponseSchema>;
export type PaConfigurationTool = z.infer<typeof paConfigurationToolSchema>;